import React from 'react';

import {
  ErrorView as Error,
  NoPermissions,
  NotFound,
  PathBuilder,
  rolesChildren,
} from '@gasbi/shared';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import { CustomerTypeEnum } from '@/features/customers/types';
import { UserPermissions } from '@/features/permissions';
import { Pages } from '@/pages';
import WorkflowBuilderRoutes from '@/pages/WorkflowBuilder/route';
import { AppRouteParam } from '@/routeParams';
import type { AppRouteObject } from '@/routeTypes';
import { redirectLegacyOrganizationPath } from '@/utils';

const DemoInteractions = React.lazy(() => import('@/pages/DemoInteractions'));

const Workflows = React.lazy(() => import('@/pages/Workflows'));
const WorkflowDefinitions = React.lazy(
  () => import('@/pages/Workflows/views/WorkflowDefinitions'),
);
const WorkflowInstances = React.lazy(
  () => import('@/pages/Workflows/views/WorkflowInstances'),
);
const BusinessProcesses = React.lazy(() => import('@/pages/BusinessProcesses'));
const BusinessProcessDetails = React.lazy(
  () => import('@/pages/BusinessProcessDetails'),
);
const BusinessProcessDetailsProgress = React.lazy(
  () => import('@/pages/BusinessProcessDetails/views/Progress'),
);
const BusinessProcessDetailsDocuments = React.lazy(
  () => import('@/pages/BusinessProcessDetails/views/Documents'),
);
const WorkflowInstanceDetail = React.lazy(
  () => import('@/pages/Workflows/views/WorkflowInstanceDetail'),
);
const WorkflowDesigner = React.lazy(
  () => import('@/pages/Workflows/views/WorkflowDesigner'),
);
const AiAssistant = React.lazy(() => import('@/pages/AiAssistant'));
const Router = React.lazy(() => import('@/layout/Router'));
const AuthCallback = React.lazy(() => import('@/pages/auth/AuthCallback'));
const NotAuthorizedPage = React.lazy(() => import('@/pages/auth/NotAuthorizedPage'));
const ChangePassword = React.lazy(() => import('@/pages/auth/ChangePassword'));
const Clients = React.lazy(() => import('@/pages/Clients'));
const ClientsList = React.lazy(
  () => import('@/pages/Clients/views/ClientsList'),
);
const ProspectiveClients = React.lazy(
  () => import('@/pages/Clients/views/ProspectiveClients'),
);
const ClientDetails = React.lazy(() => import('@/pages/ClientDetails'));
const ClientInformation = React.lazy(
  () => import('@/pages/ClientDetails/views/Information'),
);
const ClientSurveys = React.lazy(
  () => import('@/pages/ClientDetails/views/Surveys'),
);
const ClientCommunication = React.lazy(
  () => import('@/pages/ClientDetails/views/Communication'),
);
const ClientAppeals = React.lazy(
  () => import('@/pages/ClientDetails/views/Appeals'),
);
const ClientFinance = React.lazy(
  () => import('@/pages/ClientDetails/views/Finance'),
);
const ProspectiveClientDetails = React.lazy(
  () => import('@/pages/ProspectiveClientDetails'),
);
const ProspectiveClientInformation = React.lazy(
  () => import('@/pages/ProspectiveClientDetails/views/Information'),
);
const ProspectiveClientAppeals = React.lazy(
  () => import('@/pages/ProspectiveClientDetails/views/Appeals'),
);
const Counterparties = React.lazy(() => import('@/pages/Counterparties'));
const CounterpartiesList = React.lazy(
  () => import('@/pages/Counterparties/views/CounterpartiesList'),
);
const ProspectiveCounterparties = React.lazy(
  () => import('@/pages/Counterparties/views/ProspectiveCounterparties'),
);
const CounterpartyDetails = React.lazy(
  () => import('@/pages/CounterpartyDetails'),
);
const CounterpartyInformation = React.lazy(
  () => import('@/pages/CounterpartyDetails/views/Information'),
);
const CounterpartyFinance = React.lazy(
  () => import('@/pages/CounterpartyDetails/views/Finance'),
);
const CounterpartyContractsDebt = React.lazy(
  () => import('@/pages/CounterpartyDetails/views/Finance/ContractsDebt'),
);
const CounterpartyAppeals = React.lazy(
  () => import('@/pages/CounterpartyDetails/views/Appeals'),
);
const ProspectiveCounterpartyDetails = React.lazy(
  () => import('@/pages/ProspectiveCounterpartyDetails'),
);
const ProspectiveCounterpartyInformation = React.lazy(
  () => import('@/pages/ProspectiveCounterpartyDetails/views/Information'),
);
const ProspectiveCounterpartyAppeals = React.lazy(
  () => import('@/pages/ProspectiveCounterpartyDetails/views/Appeals'),
);
const Surveys = React.lazy(() => import('@/pages/Surveys'));
const TakeASurvey = React.lazy(
  () => import('@/pages/ClientDetails/views/TakeASurvey'),
);
const SurveyBuilder = React.lazy(() => import('@/pages/SurveyBuilder'));
const Appeals = React.lazy(() => import('@/pages/Appeals'));
const AppealDetails = React.lazy(
  () => import('@/pages/Appeals/components/AppealDetails'),
);
const AppealsConfig = React.lazy(() => import('@/pages/Appeals/AppealsConfig'));
const GeneralSettings = React.lazy(() => import('@/pages/GeneralSettings'));
const ContractConclusion = React.lazy(
  () => import('@/pages/ContractConclusion'),
);
const InformingSettings = React.lazy(() => import('@/pages/InformingSettings'));
const Informing = React.lazy(() => import('@/pages/Informing'));
const CampaignBuilder = React.lazy(() => import('@/pages/CampaignBuilder'));
const RootRedirect = React.lazy(() => import('@/pages/RootRedirect'));
const Employees = React.lazy(() => import('@/pages/Employees'));
const EmployeeForm = React.lazy(
  () => import('@gasbi/shared/web/Organization/forms/EmployeeForm'),
);
const EmployeesGroups = React.lazy(() => import('@/pages/EmployeesGroups'));
const EmployeeGroupDetails = React.lazy(
  () => import('@/forms/EmployeeGroupDetails'),
);

const TagManagement = React.lazy(() => import('@/pages/TagManagement'));
const BiDashboards = React.lazy(() => import('@/pages/BiDashboards'));

// Prospective and unidentified leads share the same details pages — only the list they came
// from (and so the breadcrumb/back target) differs.
const prospectiveClientDetailsChildren: AppRouteObject[] = [
  {
    path: '',
    element: <ProspectiveClientInformation />,
  },
  {
    path: Pages.CLIENT_APPEALS,
    handle: { permissions: UserPermissions.ServiceAppealsRead },
    element: <ProspectiveClientAppeals />,
    children: [
      {
        path: ':appealId',
        element: <AppealDetails />,
      },
    ],
  },
];

const prospectiveCounterpartyDetailsChildren: AppRouteObject[] = [
  {
    path: '',
    element: <ProspectiveCounterpartyInformation />,
  },
  {
    path: Pages.CLIENT_APPEALS,
    handle: { permissions: UserPermissions.ServiceAppealsRead },
    element: <ProspectiveCounterpartyAppeals />,
    children: [
      {
        path: ':appealId',
        element: <AppealDetails />,
      },
    ],
  },
];

export const routes: AppRouteObject[] = [
  {
    path: Pages.CLIENTS,
    handle: { permissions: UserPermissions.ClientsIndividualsRead },
    element: <Clients />,
    children: [
      { path: '', element: <ClientsList /> },
      {
        path: Pages.POTENTIAL,
        element: (
          <ProspectiveClients customerType={CustomerTypeEnum.Prospective} />
        ),
      },
      {
        path: Pages.UNIDENTIFIED,
        element: (
          <ProspectiveClients customerType={CustomerTypeEnum.Unidentified} />
        ),
      },
    ],
  },
  {
    path: `${Pages.CLIENTS}/:${AppRouteParam.Id}`,
    handle: { permissions: UserPermissions.ClientsIndividualsRead },
    element: <ClientDetails />,
    children: [
      {
        path: '',
        element: <ClientInformation />,
      },
      {
        path: Pages.CLIENT_FINANCE,
        element: <ClientFinance />,
      },
      {
        path: Pages.CLIENT_COMMUNICATION,
        element: <ClientCommunication />,
      },
      {
        path: Pages.CLIENT_APPEALS,
        handle: { permissions: UserPermissions.ServiceAppealsRead },
        element: <ClientAppeals />,
        children: [
          {
            path: ':appealId',
            element: <AppealDetails />,
          },
        ],
      },
      {
        path: Pages.CLIENT_APPLICATIONS,
        element: <ClientInformation />,
      },
      {
        path: Pages.CLIENT_SURVEYS,
        handle: { permissions: UserPermissions.ServiceSurveysRead },
        element: <ClientSurveys />,
      },
    ],
  },
  {
    path: `${Pages.CLIENTS}/:id/${Pages.CLIENT_SURVEYS}/:surveyId/:sessionId/${Pages.CLIENT_TAKE_A_SURVEY}`,
    handle: {
      permissions: [
        UserPermissions.ClientsIndividualsRead,
        UserPermissions.ServiceSurveysWrite,
      ],
    },
    element: <TakeASurvey />,
  },
  {
    path: `${Pages.CLIENTS}/${Pages.POTENTIAL}/:${AppRouteParam.Id}`,
    handle: { permissions: UserPermissions.ClientsIndividualsRead },
    element: (
      <ProspectiveClientDetails customerType={CustomerTypeEnum.Prospective} />
    ),
    children: prospectiveClientDetailsChildren,
  },
  {
    path: `${Pages.CLIENTS}/${Pages.UNIDENTIFIED}/:${AppRouteParam.Id}`,
    handle: { permissions: UserPermissions.ClientsIndividualsRead },
    element: (
      <ProspectiveClientDetails customerType={CustomerTypeEnum.Unidentified} />
    ),
    children: prospectiveClientDetailsChildren,
  },
  {
    path: Pages.COUNTERPARTIES,
    handle: { permissions: UserPermissions.ClientsLegalEntitiesRead },
    element: <Counterparties />,
    children: [
      { path: '', element: <CounterpartiesList /> },
      {
        path: Pages.POTENTIAL,
        element: (
          <ProspectiveCounterparties
            customerType={CustomerTypeEnum.Prospective}
          />
        ),
      },
      {
        path: Pages.UNIDENTIFIED,
        element: (
          <ProspectiveCounterparties
            customerType={CustomerTypeEnum.Unidentified}
          />
        ),
      },
    ],
  },
  {
    path: `${Pages.COUNTERPARTIES}/:id`,
    handle: { permissions: UserPermissions.ClientsLegalEntitiesRead },
    element: <CounterpartyDetails />,
    children: [
      {
        path: '',
        element: <CounterpartyInformation />,
      },
      {
        path: Pages.CLIENT_FINANCE,
        element: <CounterpartyFinance />,
      },
      {
        path: Pages.CLIENT_COMMUNICATION,
        element: <ClientCommunication />,
      },
      {
        path: Pages.CLIENT_APPEALS,
        handle: { permissions: UserPermissions.ServiceAppealsRead },
        element: <CounterpartyAppeals />,
        children: [
          {
            path: ':appealId',
            element: <AppealDetails />,
          },
        ],
      },
    ],
  },
  {
    path: `${Pages.COUNTERPARTIES}/:id/${Pages.CLIENT_FINANCE}/${Pages.CONTRACTS_DEBT}`,
    handle: { permissions: UserPermissions.ClientsLegalEntitiesRead },
    element: <CounterpartyContractsDebt />,
  },
  {
    path: `${Pages.COUNTERPARTIES}/${Pages.POTENTIAL}/:${AppRouteParam.Id}`,
    handle: { permissions: UserPermissions.ClientsLegalEntitiesRead },
    element: (
      <ProspectiveCounterpartyDetails
        customerType={CustomerTypeEnum.Prospective}
      />
    ),
    children: prospectiveCounterpartyDetailsChildren,
  },
  {
    path: `${Pages.COUNTERPARTIES}/${Pages.UNIDENTIFIED}/:${AppRouteParam.Id}`,
    handle: { permissions: UserPermissions.ClientsLegalEntitiesRead },
    element: (
      <ProspectiveCounterpartyDetails
        customerType={CustomerTypeEnum.Unidentified}
      />
    ),
    children: prospectiveCounterpartyDetailsChildren,
  },
  {
    path: Pages.SURVEYS,
    handle: { permissions: UserPermissions.ServiceSurveysRead },
    element: <Surveys />,
  },
  {
    path: `${Pages.SURVEYS}/${Pages.SURVEY_BUILDER}`,
    handle: { permissions: UserPermissions.ServiceSurveysWrite },
    element: <SurveyBuilder />,
  },
  {
    path: `${Pages.SURVEYS}/:${AppRouteParam.SurveyId}`,
    handle: { permissions: UserPermissions.ServiceSurveysRead },
    element: <SurveyBuilder />,
  },
  {
    path: `${Pages.SURVEYS}/${Pages.SURVEY_EDIT}`,
    handle: { permissions: UserPermissions.ServiceSurveysWrite },
    element: <SurveyBuilder />,
  },
  // No landing page of its own: the root opens the first sidebar entry the user may see (GSB-8540).
  {
    path: Pages.ROOT,
    element: <RootRedirect />,
  },
  /** START: Співробітники, Групи, Ролі — former tabs of «Моя організація» (GSB-8540), with its permission */
  {
    path: Pages.EMPLOYEES,
    handle: {
      permissions: [
        UserPermissions.OrganizationsOrganizationRead,
        UserPermissions.OrganizationsEmployeeRead,
      ],
    },
    element: <Employees />,
    children: [
      {
        path: PathBuilder.param(AppRouteParam.EmployeeId).build(),
        element: <EmployeeForm />,
      },
    ],
  },
  {
    // The menu also asks for employee or role read, as the EmployeeGroups API does.
    path: Pages.EMPLOYEE_GROUPS,
    handle: { permissions: UserPermissions.OrganizationsOrganizationRead },
    element: <EmployeesGroups />,
    children: [
      {
        path: PathBuilder.param(AppRouteParam.EmployeeGroupsId).build(),
        element: <EmployeeGroupDetails />,
      },
    ],
  },
  {
    path: Pages.ROLES,
    handle: {
      permissions: [
        UserPermissions.OrganizationsOrganizationRead,
        UserPermissions.OrganizationsRoleAndPermissionRead,
      ],
    },
    children: rolesChildren(false),
  },
  // Former locations of «Моя організація».
  ...[Pages.ORGANIZATION, Pages.ADMINISTRATION].map(path => ({
    path: `${path}/*`,
    element: <Navigate to={Pages.ROOT} replace />,
  })),
  /** END: Співробітники, Групи, Ролі */
  {
    path: `${Pages.APPEALS}`,
    handle: { permissions: UserPermissions.ServiceAppealsRead },
    element: <Appeals />,
    children: [
      {
        path: ':appealId',
        element: <AppealDetails />,
      },
    ],
  },
  {
    path: PathBuilder.absolute()
      .page(Pages.APPEALS)
      .page(Pages.APPEALS_SETTINGS)
      .build(),
    handle: { permissions: UserPermissions.SettingsAppealsRead },
    element: <AppealsConfig />,
  },
  {
    path: Pages.GENERAL_SETTINGS,
    handle: { permissions: UserPermissions.SettingsGeneralRead },
    element: <GeneralSettings />,
  },
  {
    path: Pages.CONTRACT_CONCLUSION,
    handle: { permissions: UserPermissions.SettingsContractConclusionRead },
    element: <ContractConclusion />,
  },
  {
    path: Pages.INFORMING_SETTINGS,
    handle: { permissions: UserPermissions.SettingsInformingRead },
    element: <InformingSettings />,
  },
  {
    path: Pages.INFORMING,
    handle: { permissions: UserPermissions.ServiceInformingRead },
    element: <Informing />,
  },
  {
    path: PathBuilder.absolute()
      .page(Pages.INFORMING)
      .page(Pages.INFORMING_CAMPAIGN_BUILDER)
      .build(),
    handle: { permissions: UserPermissions.ServiceInformingWrite },
    element: <CampaignBuilder />,
  },
  {
    path: PathBuilder.absolute().page(Pages.TAG_MANAGEMENT).build(),
    handle: { permissions: UserPermissions.ServiceTagManagementRead },
    element: <TagManagement />,
  },
  {
    path: Pages.BI_DASHBOARDS,
    handle: {
      permissions: UserPermissions.ReportsAndAnalyticsBiDashboardsRead,
    },
    element: <BiDashboards />,
  },
  {
    // No handle.permissions: Agents.Chat.Use belongs to the agents service's RBAC, which the CRM permission
    // context does not hold; the page asks the service and shows the no-access state itself (GSB-7592).
    path: Pages.AI_ASSISTANT,
    element: <AiAssistant />,
  },
  {
    path: PathBuilder.absolute()
      .page(Pages.INFORMING)
      .page(Pages.INFORMING_CAMPAIGN_BUILDER)
      .param(AppRouteParam.CampaignId)
      .build(),
    handle: { permissions: UserPermissions.ServiceInformingWrite },
    element: <CampaignBuilder />,
  },
  {
    path: Pages.CHANGE_PASSWORD,
    element: <ChangePassword />,
  },
  {
    path: 'demo',
    element: <DemoInteractions />,
  },
  ...WorkflowBuilderRoutes,
  {
    path: Pages.WORKFLOWS,
    handle: { permissions: UserPermissions.ServiceWorkflowsRead },
    element: <Workflows />,
    children: [
      { path: '', element: <WorkflowDefinitions /> },
      { path: Pages.WORKFLOWS_INSTANCES, element: <WorkflowInstances /> },
    ],
  },
  {
    path: `${Pages.WORKFLOWS}/:versionId`,
    handle: { permissions: UserPermissions.ServiceWorkflowsRead },
    element: <WorkflowDesigner />,
  },
  {
    path: `${Pages.WORKFLOWS}/${Pages.WORKFLOWS_INSTANCES}/:instanceId`,
    handle: { permissions: UserPermissions.ServiceWorkflowsRead },
    element: <WorkflowInstanceDetail />,
  },
  {
    path: `${Pages.BUSINESS_PROCESSES}`,
    handle: { permissions: UserPermissions.ServiceContractConclusionRead },
    element: <BusinessProcesses />,
  },
  {
    path: `${Pages.BUSINESS_PROCESSES}/:id`,
    handle: { permissions: UserPermissions.ServiceContractConclusionRead },
    element: <BusinessProcessDetails />,
    children: [
      { index: true, element: <BusinessProcessDetailsProgress /> },
      {
        path: `${Pages.BUSINESS_PROCESSES_DOCUMENTS}`,
        element: <BusinessProcessDetailsDocuments />,
      },
    ],
  },
  { path: '*', element: <NotFound /> },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
];

const systemRoutes: AppRouteObject[] = [
  {
    path: Pages.NO_PERMISSIONS,
    element: <NoPermissions />,
  },
  {
    path: Pages.NOT_AUTHORIZED,
    element: <NotAuthorizedPage />,
  },
];

export const appRoutes: AppRouteObject[] = [
  {
    path: Pages.AUTH_CALLBACK,
    element: <AuthCallback />,
  },
  ...systemRoutes,
  {
    element: <Router />,
    children: routes,
  },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
  { path: '*', element: <NotFound /> },
];

const withErrorElement = (routeObjects: AppRouteObject[]) =>
  routeObjects.map(route => ({ ...route, errorElement: <Error /> }));

/**
 * CRM has no organization in its URLs (GSB-8472, PO 2026-10-29). A bookmark that still starts
 * with an organization id is rewritten to the same path without it before the router reads the URL.
 */
export const getRoutes = () => {
  redirectLegacyOrganizationPath(window.location, window.history);

  return createBrowserRouter(withErrorElement(appRoutes));
};
